import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "context/OnboardingContext";
import { onboardingService } from "services/onboarding-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Checkbox, FormControlLabel, CircularProgress } from "@mui/material";
import MDButton from "components/MDButton";
import { motion } from "framer-motion";
import OnboardingLayout from "./OnboardingLayout";
import { useMaterialUIController } from "context";
import { getOnboardingThemeTokens } from "./themeTokens";

const FinalAttestation = () => {
  const navigate = useNavigate();
  const { sessionToken, claimMeta, theme } = useOnboarding();
  const [attestData, setAttestData] = useState({
    attest_data_accuracy: false,
    accept_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = getOnboardingThemeTokens({ darkMode, templateTheme: theme });

  const handleComplete = async () => {
    if (!attestData.attest_data_accuracy || !attestData.accept_terms) {
      alert("Please accept all terms and attest to data accuracy");
      return;
    }

    setLoading(true);
    try {
      await onboardingService.completeOnboarding(sessionToken, attestData);
      navigate("/onboard/success");
    } catch (err) {
      console.error("Completion error:", err);
      alert("Error completing onboarding");
    } finally {
      setLoading(false);
    }
  };

  const isReady = attestData.attest_data_accuracy && attestData.accept_terms;

  return (
    <OnboardingLayout>
      <motion.div
        initial={{ x: -10, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <MDTypography variant="h3" fontWeight="bold" mb={1}>
          {claimMeta?.claim_type === "build_device" ? "Build + Device" : claimMeta?.claim_type === "device" ? "Device" : "Build"} Final Attestation
        </MDTypography>
        <MDTypography variant="body2" color="text" mb={4}>
          One last step to activate your build.
        </MDTypography>
      </motion.div>

      <MDBox
        p={3}
        mb={4}
        sx={{
          background: "rgba(0,0,0,0.02)",
          borderRadius: "16px",
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <MDBox mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={attestData.attest_data_accuracy}
                onChange={(e) =>
                  setAttestData({ ...attestData, attest_data_accuracy: e.target.checked })
                }
                color="primary"
                sx={{
                  background: t.softSurface,
                  borderRadius: "16px",
                  border: `1px solid ${t.borderSubtle}`,
                }}
              />
            }
            label={
              <MDTypography variant="body2" color="text">
                I attest that all the information provided is accurate and complete.
              </MDTypography>
            }
          />
        </MDBox>
        <MDBox>
          <FormControlLabel
            control={
              <Checkbox
                checked={attestData.accept_terms}
                onChange={(e) => setAttestData({ ...attestData, accept_terms: e.target.checked })}
                color="primary"
                sx={{
                  background: t.softSurface,
                  borderRadius: "16px",
                  border: `1px solid ${t.borderSubtle}`,
                }}
              />
            }
            label={
              <MDTypography variant="body2" color="text">
                I accept the Terms of Service and Privacy Policy.
              </MDTypography>
            }
          />
        </MDBox>
      </MDBox>

      <motion.div whileHover={{ scale: isReady ? 1.01 : 1 }} whileTap={{ scale: isReady ? 0.99 : 1 }}>
        <MDButton
          variant="gradient"
          color="info"
          fullWidth
          onClick={handleComplete}
          disabled={loading || !isReady}
          size="large"
          sx={{ borderRadius: "12px", py: 1.5 }}
        >
          {loading ? (
            <MDBox display="flex" alignItems="center">
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Activating...
            </MDBox>
          ) : (
            "Activate My Build"
          )}
        </MDButton>
      </motion.div>
    </OnboardingLayout>
  );
};

export default FinalAttestation;
